import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ImageIcon, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getApiRoute } from '@/lib/config/urls';
import { safeJsonParseRecord } from '@/lib/utils/safe-json';

interface ImageUploadDropzoneProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  description?: string;
  /** 'thumbnail' gives a taller preview box; 'banner' is shorter/wider; 'avatar' is square */
  aspectRatio?: 'thumbnail' | 'banner' | 'avatar';
  /** R2 folder to store the file in — 'thumbnails' | 'avatars' | 'banners' (default: 'thumbnails') */
  folder?: 'thumbnails' | 'avatars' | 'banners';
  className?: string;
}

export function ImageUploadDropzone({
  value,
  onChange,
  description,
  aspectRatio = 'thumbnail',
  folder = 'thumbnails',
  className,
}: ImageUploadDropzoneProps) {
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      // Read the Supabase access token directly from localStorage to avoid the
      // navigator.locks deadlock in Supabase JS v2.90+
      let token: string | null = null;
      try {
        const sbKey = Object.keys(localStorage).find(
          k => k.startsWith('sb-') && k.endsWith('auth-token')
        );
        if (sbKey) {
          const raw = localStorage.getItem(sbKey);
          if (raw) {
            const parsed = safeJsonParseRecord(raw);
            token = parsed && typeof parsed.access_token === 'string' ? parsed.access_token : null;
          }
        }
      } catch {
        token = null;
      }

      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const url = getApiRoute('/api/admin/upload');
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as any;
        const msg = errBody?.error?.message ?? errBody?.message ?? 'Upload failed';
        throw new Error(msg);
      }

      const result = await res.json() as any;
      // Handle both { data: { url } } (wrapped) and { url } (direct) response shapes
      const uploadedUrl: string = result?.data?.url ?? result?.url ?? '';
      if (!uploadedUrl) throw new Error('No URL returned from server');

      onChange(uploadedUrl);
      toast.success('Image uploaded');
    } catch (err: any) {
      console.error('Image upload error:', err);
      toast.error(err?.message ?? 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, folder]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5 MB
    disabled: isUploading,
  });

  // Show first file rejection reason if any
  const rejectionError = fileRejections[0]?.errors[0]?.message;

  const previewClass = aspectRatio === 'banner' ? 'h-24'
    : aspectRatio === 'avatar' ? 'h-24 w-24 rounded-full mx-auto'
    : 'h-32';

  const rootClass = cn(
    'relative flex w-full cursor-pointer items-center justify-center rounded-md border-2 border-dashed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/20',
    previewClass,
    isDragActive ? 'border-primary bg-primary/5' : '',
    isUploading && 'pointer-events-none opacity-60',
    value && 'border-solid border-muted/60 overflow-hidden',
  );

  const rootProps = getRootProps({
    className: rootClass,
    role: 'presentation',
    tabIndex: 0,
  });

  return (
    <div className={cn('space-y-1.5', className)}>
      <div {...rootProps}>
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-xs">Uploading…</p>
          </div>
        ) : value ? (
          <>
            <img
              src={value}
              alt="Preview"
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded-[inherit]">
              <div className="flex flex-col items-center gap-1 text-white">
                <Upload className="h-5 w-5" />
                <p className="text-xs font-medium">Replace image</p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground px-4 py-2 text-center pointer-events-none">
            <ImageIcon className="h-7 w-7 opacity-35" aria-hidden />
            <div className="space-y-1">
              <p className="text-sm font-medium leading-none">
                {isDragActive ? 'Drop to upload' : 'Drag & drop or click to upload'}
              </p>
              <p className="text-xs opacity-60">
                {description ?? 'JPEG, PNG, WebP · max 5 MB'}
              </p>
            </div>
          </div>
        )}
      </div>

      {value && !isUploading && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
        >
          <X className="h-3 w-3" />
          Remove image
        </button>
      )}

      {rejectionError && (
        <p className="text-xs text-destructive">{rejectionError}</p>
      )}
    </div>
  );
}
